package com.anjuros.website.redesign.sections.pricing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdEyebrow
import com.anjuros.website.redesign.components.RdLink
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.styles.PricingBadgeSoftStyle
import com.anjuros.website.redesign.styles.PricingBadgeStyle
import com.anjuros.website.redesign.styles.PricingBuilderGridStyle
import com.anjuros.website.redesign.styles.PricingBuilderSectionStyle
import com.anjuros.website.redesign.styles.PricingFeaturesStyle
import com.anjuros.website.redesign.styles.PricingFieldsetStyle
import com.anjuros.website.redesign.styles.PricingFromStyle
import com.anjuros.website.redesign.styles.PricingOptionHeadStyle
import com.anjuros.website.redesign.styles.PricingOptionSelectedStyle
import com.anjuros.website.redesign.styles.PricingOptionStyle
import com.anjuros.website.redesign.styles.PricingOptionsThreeStyle
import com.anjuros.website.redesign.styles.PricingOptionsTwoStyle
import com.anjuros.website.redesign.styles.PricingPlusStyle
import com.anjuros.website.redesign.styles.PricingPriceStyle
import com.anjuros.website.redesign.styles.PricingRadioStyle
import com.anjuros.website.redesign.styles.PricingStepTitleStyle
import com.anjuros.website.redesign.styles.PricingStepsStyle
import com.anjuros.website.redesign.styles.RdH2Style
import com.anjuros.website.redesign.styles.RdH3Style
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.rdList
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Small
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * Pick a website and a care plan; the quote sheet beside them updates as you choose.
 * Starts on Customised + Full Service so the quote is never empty.
 */
@Composable
fun PricingBuilderSection() {
    var website by remember { mutableStateOf(WebsiteOption.CUSTOM) }
    var plan by remember { mutableStateOf(CarePlan.FULL) }
    val label = LocalText(RdText.PRICING_BUILDER_LABEL)

    RdSection(
        id = "prices",
        modifier = PricingBuilderSectionStyle.toModifier().attrsModifier { attr("aria-label", label) },
        wrapModifier = PricingBuilderGridStyle.toModifier(),
    ) {
        Div(PricingStepsStyle.toModifier().toAttrs()) {
            Fieldset(PricingFieldsetStyle.toModifier().toAttrs()) {
                StepLegend(RdText.PRICING_STEP1_EYEBROW, RdText.PRICING_STEP1_TITLE)
                Div(PricingOptionsTwoStyle.toModifier().toAttrs()) {
                    WebsiteOption.entries.forEach { option ->
                        WebsiteCard(option, selected = option == website) { website = option }
                    }
                }
            }
            // Keeps the old #maintenance link from other pages landing on the care plans.
            Fieldset(PricingFieldsetStyle.toModifier().attrsModifier { attr("id", "maintenance") }.toAttrs()) {
                StepLegend(RdText.PRICING_STEP2_EYEBROW, RdText.PRICING_STEP2_TITLE)
                P { Text(LocalText(RdText.PRICING_STEP2_TEXT)) }
                Div(PricingOptionsThreeStyle.toModifier().toAttrs()) {
                    CarePlan.entries.forEach { option ->
                        PlanCard(option, selected = option == plan) { plan = option }
                    }
                }
            }
        }
        PricingQuoteSheet(website, plan)
    }
}

@Composable
private fun StepLegend(eyebrow: RdText, title: RdText) {
    Legend {
        RdEyebrow(eyebrow)
        H2(RdH2Style.toModifier().then(PricingStepTitleStyle.toModifier()).toAttrs()) {
            Text(LocalText(title))
        }
    }
}

@Composable
private fun WebsiteCard(option: WebsiteOption, selected: Boolean, onSelect: () -> Unit) {
    OptionCard("website", option.name, selected, onSelect) {
        Span(PricingBadgeStyle.toModifier().then(PricingBadgeSoftStyle.toModifier()).toAttrs()) { Text(LocalText(option.badge)) }
        OptionHead(option.title)
        P(PricingPriceStyle.toModifier().toAttrs()) {
            if (option.fromPrice) Span(PricingFromStyle.toModifier().toAttrs()) { Text(LocalText(RdText.PRICE_FROM)) }
            Text(LocalText(option.price))
            Small { Text(LocalText(RdText.PRICE_ONE_OFF)) }
        }
        P { Text(LocalText(option.text)) }
        Features(option.features)
        val link = option.link
        val linkPath = option.linkPath
        if (link != null && linkPath != null) RdLink(linkPath) { Text(LocalText(link)) }
    }
}

@Composable
private fun PlanCard(plan: CarePlan, selected: Boolean, onSelect: () -> Unit) {
    OptionCard("plan", plan.name, selected, onSelect) {
        plan.badge?.let { Span(PricingBadgeStyle.toModifier().toAttrs()) { Text(LocalText(it)) } }
        OptionHead(plan.title)
        P(PricingPriceStyle.toModifier().toAttrs()) {
            Text(LocalText(plan.price))
            Small { Text(LocalText(RdText.PRICE_PER_MONTH)) }
        }
        P { Text(LocalText(plan.text)) }
        Features(plan.features, includesPrevious = plan.includesPrevious)
    }
}

/** A card wrapping a visually hidden radio button, so it works with keyboard and screen readers. */
@Composable
private fun OptionCard(group: String, value: String, selected: Boolean, onSelect: () -> Unit, content: @Composable () -> Unit) {
    val inputId = "$group-${value.lowercase()}"
    Label(
        forId = inputId,
        attrs = PricingOptionStyle.toModifier().thenIf(selected, PricingOptionSelectedStyle.toModifier()).toAttrs(),
    ) {
        Input(InputType.Radio) {
            id(inputId)
            name(group)
            checked(selected)
            onChange { onSelect() }
        }
        content()
    }
}

@Composable
private fun OptionHead(title: RdText) {
    Div(PricingOptionHeadStyle.toModifier().toAttrs()) {
        H3(RdH3Style.toModifier().toAttrs()) { Text(LocalText(title)) }
        Span(PricingRadioStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs())
    }
}

@Composable
private fun Features(features: RdText, includesPrevious: RdText? = null) {
    Ul(PricingFeaturesStyle.toModifier().toAttrs()) {
        includesPrevious?.let { Li(PricingPlusStyle.toModifier().toAttrs()) { Text(LocalText(it)) } }
        rdList(LocalText(features)).forEach { Li { Text(it) } }
    }
}
